import type { StateCreator } from 'zustand';
import type { ItineraryState } from '../itinerary-store';

/** Registry default for in-app haptics. Exported so callers that need
 *  to compare against the default (UserPrefsSync migration arm, the
 *  persistedDefaults builder in itinerary-store.ts) stay in lockstep
 *  if the default ever changes — pre-fix the migration hardcoded
 *  `!== true` and would have inverted silently on a default flip.
 *  Mirrors the `defaultLayerPrefs()` / `defaultReviewLayerPrefs()`
 *  exports from their respective registry files. */
export const DEFAULT_HAPTICS_ENABLED = true;

export interface ViewSlice {
  /** Trip ids the user has marked as favourite. Local-only preference. */
  favoriteTripIds: string[];
  /** Dismissal timestamps (epoch-ms) for in-app upsell banners, keyed
   *  by upsell id. Local-only — no server round-trip. Used by the
   *  trips-list "X of 3 free trips" nudge (ADR-0062) to honor the
   *  user's dismissal across reloads. */
  dismissedUpsells: Record<string, number>;
  /** Per-trip last viewed day index — persisted so the next cold start
   *  lands the user on the day they had open when they closed the app.
   *  Keyed by trip id so resuming Trip A on day 4 doesn't clobber Trip
   *  B's cursor. See ADR-0068. */
  lastViewedDayIndex: Record<string, number>;
  /** User preference: in-app haptics on/off. Default true. OR-ed with
   *  the system Reduce Motion flag at the HapticsProvider mount — either
   *  source can suppress. Synced through UserPreferences. ADR-0079. */
  hapticsEnabled: boolean;
  /** User-customized height (px) for the trip-detail hero / map area.
   *  `null` = use the responsive default (aspectRatio 1.4 / largeHeight
   *  520) until the user drag-resizes. Local-only UI pref — persisted
   *  across cold starts but not synced to the server. */
  heroHeight: number | null;
  /** ADR-0091 — when true, the new-trip spotlight walkthrough never
   *  auto-starts. Synced through UserPreferences so a dismissal on one
   *  device carries across. Default false. */
  tutorialDisabled: boolean;
  /** ADR-0091 — ISO timestamp the user last finished the walkthrough,
   *  or null if never. Synced through UserPreferences. */
  tutorialSeenAt: string | null;
  /** ADR-0091 — the trip whose walkthrough is still pending (armed on a
   *  manual create, cleared when the tour is finished / skipped /
   *  disabled). Local-persisted so a force-quit mid-tour re-shows it on
   *  the next open of that trip. Not synced — it's a per-device
   *  onboarding marker. */
  tutorialPendingTripId: string | null;
  toggleFavoriteTrip: (tripId: string) => void;
  dismissUpsell: (id: string) => void;
  setLastViewedDayIndex: (tripId: string, index: number) => void;
  setHapticsEnabled: (enabled: boolean) => void;
  setHeroHeight: (height: number | null) => void;
  setTutorialDisabled: (disabled: boolean) => void;
  markTutorialSeen: () => void;
  setTutorialPending: (tripId: string | null) => void;
}

export const createViewSlice: StateCreator<ItineraryState, [], [], ViewSlice> = (set, get) => ({
  favoriteTripIds: [],
  dismissedUpsells: {},
  lastViewedDayIndex: {},
  hapticsEnabled: DEFAULT_HAPTICS_ENABLED,
  heroHeight: null,
  tutorialDisabled: false,
  tutorialSeenAt: null,
  tutorialPendingTripId: null,
  toggleFavoriteTrip: (tripId) => {
    set((s) => ({
      favoriteTripIds: s.favoriteTripIds.includes(tripId)
        ? s.favoriteTripIds.filter((id) => id !== tripId)
        : [...s.favoriteTripIds, tripId],
    }));
    get().markUserPrefsDirty();
  },
  dismissUpsell: (id) =>
    set((s) => ({
      dismissedUpsells: { ...s.dismissedUpsells, [id]: Date.now() },
    })),
  setLastViewedDayIndex: (tripId, index) =>
    set((s) => {
      // Skip the write if the value isn't changing — avoids unnecessary
      // re-renders for any consumer that subscribed to the map.
      if (s.lastViewedDayIndex[tripId] === index) return s;
      return { lastViewedDayIndex: { ...s.lastViewedDayIndex, [tripId]: index } };
    }),
  setHapticsEnabled: (enabled) => {
    set({ hapticsEnabled: enabled });
    get().markUserPrefsDirty();
  },
  // Local-only pref (like lastViewedDayIndex) — no markUserPrefsDirty.
  setHeroHeight: (height) => set({ heroHeight: height }),
  setTutorialDisabled: (disabled) => {
    set({ tutorialDisabled: disabled });
    get().markUserPrefsDirty();
  },
  markTutorialSeen: () => {
    set({ tutorialSeenAt: new Date().toISOString() });
    get().markUserPrefsDirty();
  },
  // Local-only marker (not synced) — no markUserPrefsDirty.
  setTutorialPending: (tripId) => set({ tutorialPendingTripId: tripId }),
});
